import { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  ROLES,
  isAdminRole,
  isFinanceRole,
  normalizeRole,
} from '../constants/membership';

const DEFAULT_ROLE = ROLES.MEMBER;

export default function useAuthRole() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(DEFAULT_ROLE);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      setLoading(true);
      setError(null);
      setUser(nextUser);

      if (!nextUser) {
        setRole(DEFAULT_ROLE);
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, 'users', nextUser.uid);
        const snap = await getDoc(userRef);
        const existing = snap.exists() ? snap.data() : {};
        const nextRole = normalizeRole(existing.role);

        // Keep a baseline profile for authenticated users.
        if (!snap.exists()) {
          await setDoc(
            userRef,
            {
              name: nextUser.displayName || '',
              email: nextUser.email || '',
              role: DEFAULT_ROLE,
              memberId: '',
              createdAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        setRole(nextRole);
        setProfile({ uid: nextUser.uid, ...existing, role: nextRole });
      } catch (err) {
        setError(err);
        setRole(DEFAULT_ROLE);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const isAuthenticated = Boolean(user);

  return useMemo(
    () => ({
      user,
      role,
      profile,
      loading,
      error,
      isAuthenticated,
      isAdmin: isAdminRole(role),
      isTreasurer: role === ROLES.TREASURER || isFinanceRole(role),
      isMember: role === ROLES.MEMBER || role === ROLES.STUDENT,
      isStaff: role === ROLES.STAFF,
      isSuperAdmin: role === ROLES.SUPER_ADMIN,
    }),
    [user, role, profile, loading, error, isAuthenticated]
  );
}
